import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Sparkles, Newspaper, CalendarDays, Users, Plane, ClipboardCheck, KanbanSquare, Target,
  ShieldCheck, Settings, Menu, LogOut, Plus, Briefcase, Wallet, Megaphone, Search, History, Crown, AlertTriangle, Lock, Link2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { PersonAvatar } from "@/components/common";

const NAV = [
  { group: "Overview", items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
    { to: "/briefing", label: "Daily briefing", icon: Newspaper, id: "briefing" },
    { to: "/briefing/history", label: "Briefing history", icon: History, id: "briefing-history" },
  ]},
  { group: "AI team", items: [
    { to: "/ai-ceo", label: "AI CEO", icon: Sparkles, id: "ai-ceo" },
    { to: "/cfo", label: "AI CFO", icon: Wallet, id: "cfo", entitlement: "ai_cfo" },
    { to: "/marketing", label: "Marketing manager", icon: Megaphone, id: "marketing", entitlement: "ai_marketing" },
    { to: "/seo", label: "SEO specialist", icon: Search, id: "seo", entitlement: "ai_seo" },
  ]},
  { group: "Work", items: [
    { to: "/calendar", label: "Calendar", icon: CalendarDays, id: "calendar" },
    { to: "/availability", label: "Share availability", icon: Link2, id: "availability" },
    { to: "/tasks", label: "Tasks", icon: KanbanSquare, id: "tasks" },
    { to: "/goals", label: "Goals", icon: Target, id: "goals" },
  ]},
  { group: "People", items: [
    { to: "/people", label: "Staff directory", icon: Users, id: "people" },
    { to: "/leave", label: "Leave", icon: Plane, id: "leave" },
    { to: "/onboarding", label: "Onboarding", icon: ClipboardCheck, id: "onboarding" },
  ]},
  { group: "Admin", items: [
    { to: "/approvals", label: "Approvals", icon: ShieldCheck, id: "approvals" },
    { to: "/settings", label: "Settings & team", icon: Settings, id: "settings" },
  ]},
];

const PLATFORM_NAV = {
  group: "Platform",
  items: [{ to: "/admin", label: "Platform admin", icon: Crown, id: "admin" }],
};

function NavList({ onNavigate, isPlatformAdmin, entitlements }) {
  const groups = isPlatformAdmin ? [...NAV, PLATFORM_NAV] : NAV;
  const isLocked = (item) => item.entitlement && entitlements && !entitlements.entitlements?.[item.entitlement];
  return (
    <nav className="space-y-5">
      {groups.map((g) => (
        <div key={g.group}>
          <p className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{g.group}</p>
          <div className="space-y-0.5">
            {g.items.map((item) => {
              const { to, label, icon: Icon, id } = item;
              const locked = isLocked(item);
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/briefing"}
                  onClick={onNavigate}
                  data-testid={`sidebar-nav-${id}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                      isActive ? "bg-primary text-primary-foreground font-medium" : "text-foreground/80 hover:bg-muted"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  <span className="flex-1 truncate">{label}</span>
                  {locked && <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-label="Upgrade required" />}
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}

function SubscriptionBanner({ ent }) {
  if (!ent) return null;
  const s = ent.status;
  if (s === "past_due") {
    return (
      <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 py-2 flex items-center gap-2" data-testid="sub-banner-past-due">
        <AlertTriangle className="h-4 w-4" />
        <span>Payment issue on your subscription \u2014 some features may be limited.</span>
        <Link to="/settings?tab=billing" className="ml-auto font-medium underline">Fix billing</Link>
      </div>
    );
  }
  if (s === "trial_expired" || s === "canceled") {
    return (
      <div className="bg-rose-50 border-b border-rose-200 text-rose-900 text-sm px-4 py-2 flex items-center gap-2" data-testid="sub-banner-expired">
        <AlertTriangle className="h-4 w-4" />
        <span>Your {s === "canceled" ? "subscription has ended" : "free trial has ended"} \u2014 pick a plan to unlock the AI team.</span>
        <Link to="/settings?tab=billing" className="ml-auto font-medium underline">See plans</Link>
      </div>
    );
  }
  if (s === "trialing" && ent.current_period_end) {
    const days = Math.max(0, Math.ceil((new Date(ent.current_period_end) - new Date()) / (1000 * 60 * 60 * 24)));
    if (days <= 5) {
      return (
        <div className="bg-sky-50 border-b border-sky-200 text-sky-900 text-sm px-4 py-2 flex items-center gap-2" data-testid="sub-banner-trial">
          <Sparkles className="h-4 w-4" />
          <span>{days === 0 ? "Trial ends today" : `${days} day${days === 1 ? "" : "s"} left in your free trial`} \u2014 upgrade any time.</span>
          <Link to="/settings?tab=billing" className="ml-auto font-medium underline">Upgrade</Link>
        </div>
      );
    }
  }
  return null;
}

function Brand({ company }) {
  return (
    <div className="flex items-center gap-3 px-3">
      <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center">
        <Briefcase className="h-4 w-4 text-primary-foreground" />
      </div>
      <div className="min-w-0">
        <p className="font-display font-semibold leading-tight">Virtual CEO</p>
        <p className="text-xs text-muted-foreground truncate" data-testid="sidebar-company-name">{company?.name}</p>
      </div>
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isPlatformAdmin = !!user?.is_platform_admin;

  return (
    <div className="min-h-screen bg-background">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[264px] flex-col border-r border-border bg-card">
        <div className="py-5"><Brand company={user.company} /></div>
        <ScrollArea className="flex-1 px-3"><NavList isPlatformAdmin={isPlatformAdmin} entitlements={user.entitlements} /></ScrollArea>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-1.5">
            <PersonAvatar name={user.name} src={user.picture} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user.role}{isPlatformAdmin ? " \u00b7 platform admin" : ""}</p>
            </div>
            <Button variant="ghost" size="icon" aria-label="Log out" onClick={async () => { await logout(); navigate("/login"); }} data-testid="sidebar-logout-button">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[280px] p-0 bg-card">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="py-5"><Brand company={user.company} /></div>
          <ScrollArea className="h-[calc(100vh-90px)] px-3"><NavList onNavigate={() => setOpen(false)} isPlatformAdmin={isPlatformAdmin} entitlements={user.entitlements} /></ScrollArea>
        </SheetContent>
      </Sheet>

      <div className="lg:pl-[264px]">
        <SubscriptionBanner ent={user.entitlements} />
        <header className="sticky top-0 z-30 h-14 bg-card border-b border-border flex items-center gap-3 px-4 sm:px-6 lg:px-8">
          <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu" onClick={() => setOpen(true)} data-testid="topbar-menu-button">
            <Menu className="h-5 w-5" />
          </Button>
          <p className="text-sm font-medium truncate lg:hidden" data-testid="topbar-company-name">{user.company?.name}</p>
          <div className="ml-auto flex items-center gap-2">
            {location.pathname !== "/ai-ceo" && (
              <Button size="sm" variant="secondary" onClick={() => navigate("/ai-ceo")} data-testid="topbar-ask-ceo-button" className="hidden sm:inline-flex">
                <Sparkles className="h-4 w-4 mr-1.5 text-primary" /> Ask the CEO
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" data-testid="topbar-create-button"><Plus className="h-4 w-4 sm:mr-1" /><span className="hidden sm:inline">Create</span></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate("/calendar?new=1")} data-testid="create-menu-meeting">Meeting</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/tasks?new=1")} data-testid="create-menu-task">Task</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/leave?new=1")} data-testid="create-menu-leave">Leave request</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid="topbar-user-menu">
                  <PersonAvatar name={user.name} src={user.picture} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="text-sm">{user.name}</p>
                  <p className="text-xs text-muted-foreground font-normal truncate">{user.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/settings")}>Settings & team</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings?tab=billing")}>Billing</DropdownMenuItem>
                {isPlatformAdmin && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="user-menu-admin">Platform admin</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="user-menu-logout">Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-7 max-w-[1400px]">
          <div key={location.pathname} className="page-enter"><Outlet /></div>
        </main>
      </div>
    </div>
  );
}
